import { CoreButtonView, CoreImageView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TRowComponentProps, TViewCommonOption, TWidgetCommonOption } from '@libs/types';
import React, { MouseEventHandler } from 'react';
import { TRowOption } from '../../../types/options';
import { SemanticColor, semanticColors } from '../../../theme/colors';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { VISUAL_ICON_MAP } from '../../Image/styles/visualUtils';
import { VisualView } from '../../Image/VisualView';
import styles from './styles/rowRegular.module.css';

export type TRowRegularViewProps = {
    widget?: TCoreButtonViewProps['widget'];
    uid?: string;
    title?: string;
    subtitle?: string;
    metaText?: string;
    topText?: string;
    value?: boolean;
    iconName?: string;
    flagIcon?: string;
    avatar?: string;
    subtitleIconName?: string;
    iconHierarchy?: TRowOption['iconHierarchy'];
    iconVariant?: TRowOption['iconVariant'];
    metaVariant?: TRowOption['metaVariant'];
    metaHierarchy?: TRowOption['metaHierarchy'];
    variant?: 'CHECK';
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: MouseEventHandler<HTMLButtonElement>;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
} & Pick<TRowComponentProps, 'isDisabled' | 'isHide' | 'isError'>;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: semanticColors[SemanticColor.FillSecondary],
    onMain: semanticColors[SemanticColor.TextPrimary],
    fillContainer: true,
    borderRadius: 6,
    gap: 0,
    padding: { top: 16, right: 24, bottom: 16, left: 24 },
    cursorDisabled: 'default',
    textAlign: 'start',
    disabled: {
        main: semanticColors[SemanticColor.FillSecondary],
        onMain: semanticColors[SemanticColor.TextPrimaryDisabled],
    },
    hovered: {
        main: semanticColors[SemanticColor.FillSecondaryHovered],
    },
    pressed: {
        main: semanticColors[SemanticColor.FillSecondaryPressed],
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: semanticColors[SemanticColor.TextPrimary],
    disabled: {
        main: semanticColors[SemanticColor.TextPrimaryDisabled],
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: semanticColors[SemanticColor.TextSecondary],
    margin: { top: 4 },
    disabled: {
        main: semanticColors[SemanticColor.TextPrimaryDisabled],
    },
};

const META_STYLE_PROPS_SECONDARY: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: semanticColors[SemanticColor.TextSecondary],
    disabled: {
        main: semanticColors[SemanticColor.TextPrimaryDisabled],
    },
};

const META_STYLE_PROPS_PRIMARY: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: semanticColors[SemanticColor.TextPrimary],
    disabled: {
        main: semanticColors[SemanticColor.TextPrimaryDisabled],
    },
};

export const RowRegularView = ({
    widget,
    uid = '',
    title,
    subtitle,
    metaText,
    topText,
    value = false,
    iconName,
    flagIcon,
    avatar,
    subtitleIconName,
    iconHierarchy,
    iconVariant,
    metaVariant,
    metaHierarchy,
    variant,
    margin,
    isDisabled,
    isError,
    isHide,
    handleClick,
    parentStyleNames = [],
}: TRowRegularViewProps) => {
    const hasLeadingIcon = !variant && (iconName || avatar || flagIcon);
    const effectiveMetaHierarchy = metaHierarchy ?? 'secondary';
    const metaStyleProps = effectiveMetaHierarchy === 'secondary' ? META_STYLE_PROPS_SECONDARY : META_STYLE_PROPS_PRIMARY;

    const renderTrailing = () => {
        if (variant === 'CHECK' && value) {
            return (
                <CoreImageView
                    src={getHref('ic_check')}
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'rowRegularCheck']}
                    styleProps={{ width: 24, height: 24, ...VISUAL_ICON_MAP.simple.emphasised }}
                />
            );
        }
        if (metaText) {
            void metaVariant;
            return (
                <CoreTextView
                    uid={'meta-' + uid}
                    text={metaText}
                    as="span"
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="rowRegularMeta"
                    styleProps={metaStyleProps}
                />
            );
        }
        return null;
    };

    return (
        <CoreButtonView
            widget={widget ?? ({ uid: uid, data: {}, options: {} } as TCoreButtonViewProps['widget'])}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                margin: margin,
            }}
            parentStyleNames={parentStyleNames}
            styleName="rowRegular"
            onClick={handleClick}
        >
            {hasLeadingIcon && (
                <VisualView
                    uid={`icon-${uid}`}
                    iconName={iconName}
                    avatar={avatar}
                    flagIcon={flagIcon}
                    iconHierarchy={iconHierarchy}
                    iconVariant={iconVariant}
                    size={'small'}
                    isDisabled={isDisabled}
                    rightMargin
                    parentStyleNames={parentStyleNames}
                    styleName="rowRegularIcon"
                />
            )}
            <div className={styles.rowContent}>
                <ConditionalRender condition={!!topText}>
                    <CoreTextView
                        uid={'topText-' + uid}
                        text={topText}
                        as="span"
                        isDisabled={isDisabled}
                        isError={isError}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTopText"
                        styleProps={{
                            ...TextStyle.secondary,
                            main: semanticColors[SemanticColor.TextSecondary],
                            margin: { bottom: 4 },
                            disabled: {
                                main: semanticColors[SemanticColor.TextPrimaryDisabled],
                            },
                            error: {
                                main: semanticColors[SemanticColor.TextError],
                            },
                        }}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!!title}>
                    <CoreTextView
                        uid={'title-' + uid}
                        text={title}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTitle"
                        styleProps={TITLE_STYLE_PROPS}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!!subtitle}>
                    <div className={styles.subtitleRow}>
                        <ConditionalRender condition={!!subtitleIconName}>
                            <CoreImageView
                                src={getHref(subtitleIconName)}
                                isDisabled={isDisabled}
                                parentStyleNames={parentStyleNames}
                                styleName={'rowRegularSubtitleIcon'}
                                styleProps={{
                                    width: 16,
                                    height: 16,
                                    iconColor: semanticColors[SemanticColor.IconSecondary],
                                    margin: { right: 8, top: 4 },
                                }}
                            />
                        </ConditionalRender>
                        <CoreTextView
                            uid={'subtitle-' + uid}
                            text={subtitle}
                            as="span"
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowRegularSubtitle"
                            styleProps={SUBTITLE_STYLE_PROPS}
                        />
                    </div>
                </ConditionalRender>
            </div>
            {renderTrailing()}
        </CoreButtonView>
    );
};
